import { ArrowLeft, ArrowRight, Mail } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../components/Button/Button';
import FlexibleInput from '../../components/Input/Input';
import { Error, Success } from '../../config/helpers/toast-helper';
import { useAffiliateUserStore } from '../../store/affiliate-user';

const ForgotPassword = () => {
  const { verificationCode, loading } = useAffiliateUserStore();
  const navigate = useNavigate();

  // Form fields
  const [email, setEmail] = useState('');

  // Field validation states
  const [errors, setErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  // Check if email is pre-filled from localStorage
  useEffect(() => {
    const storedEmail = localStorage.getItem('affiliateResetEmail');
    if (storedEmail) {
      setEmail(storedEmail);
    }
  }, []);

  // Validation functions
  const validateEmail = (value) => {
    if (!value.trim()) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return 'Please enter a valid email address';
    return '';
  };

  // Real-time validation
  useEffect(() => {
    const newErrors = {};
    if (touchedFields.email) newErrors.email = validateEmail(email);
    setErrors(newErrors);
  }, [email, touchedFields]);

  const handleBlur = (field) => {
    setTouchedFields((prev) => ({ ...prev, [field]: true }));
  };

  const handleSubmit = async () => {
    // Mark all fields as touched for validation
    setTouchedFields({ email: true });

    // Validate email
    const emailError = validateEmail(email);

    if (emailError) {
      setErrors({ email: emailError });
      Error('Please enter a valid email address');
      return;
    }

    try {
      const payload = {
        email: email.trim().toLowerCase(),
        type: 'reset-password',
      };

      await verificationCode(payload);
      Success('Reset code sent to your email');

      // Store email for reset password page
      localStorage.setItem('affiliateResetEmail', email.trim().toLowerCase());

      // Redirect to reset password page
      navigate('/auth/reset-password');
    } catch (error) {
      console.error('Forgot password error:', error);
      Error(
        error?.response?.data?.message ||
          'Failed to send reset code. Please try again.'
      );
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const isFormValid = !validateEmail(email);
  const isEmailPrefilled = localStorage.getItem('affiliateResetEmail');

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 -left-4 w-72 h-72 bg-green-500 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob"></div>
        <div className="absolute top-0 -right-4 w-72 h-72 bg-green-400 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-green-600 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob animation-delay-4000"></div>
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-screen px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-gradient-to-r from-green-400/20 to-green-500/20 rounded-2xl flex items-center justify-center mb-6">
              <Mail className="h-8 w-8 text-green-400" />
            </div>
            <h2 className="text-4xl font-bold text-white mb-2">
              Reset Password
            </h2>
            <p className="text-gray-400">
              {isEmailPrefilled
                ? "We'll send a reset code to your email"
                : 'Enter your email to receive a reset code'}
            </p>
          </div>

          <div className="bg-gray-800/30 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-gray-700/50">
            <div className="space-y-6">
              <FlexibleInput
                placeholder="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => handleBlur('email')}
                onKeyPress={handleKeyPress}
                icon={Mail}
                iconClass="text-green-400"
                error={errors.email}
                touched={touchedFields.email}
                disabled={isEmailPrefilled}
                className="bg-gray-800/50 border-gray-600 text-white placeholder:text-gray-400 focus:border-green-400 focus:ring-green-400/20"
              />

              <Button
                text="Send Reset Code"
                onClick={handleSubmit}
                loading={loading}
                disabled={!isFormValid}
                className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white border-0 shadow-lg shadow-green-500/25 hover:shadow-green-500/40 transition-all duration-300"
                size="large"
                icon={ArrowRight}
                iconPosition="right"
              />

              <div className="text-center">
                <Link
                  to="/auth/login"
                  className="inline-flex items-center text-sm text-gray-400 hover:text-green-400 transition-colors font-medium"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Login
                </Link>
              </div>
            </div>
          </div>

          <div className="text-center">
            <p className="text-xs text-gray-500">
              Remember your password?{' '}
              <Link
                to="/auth/login"
                className="text-green-400 hover:text-green-300 font-medium transition-colors"
              >
                Sign in here
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
